import React, { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { Layout, Menu, Input, Tooltip } from "antd";
import * as Icons from "@ant-design/icons";
import { PushpinOutlined, PushpinFilled } from "@ant-design/icons";
import { ThemeContext } from "./context/ThemeContext";
import { useFavorites } from "./context/FavoritesContext";
import menuConfig from "./menuConfig";
import "./SideMenu.css";
import { useNavigate, Link } from "react-router-dom";
import { getPathFromKey } from "./routes";


const { Sider } = Layout;

const DotIcon = ({ color }) => (
    <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill={color}
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginRight: 6, marginTop: 3, flexShrink: 0 }}
    >
        <circle cx="5" cy="5" r="5" />
    </svg>
);

// Modal açan özel key'ler (route yerine callback çağırılacak)
const MODAL_KEYS = ['item-type-settings'];

/**
 * Menü yapılandırmasını özyineli (recursive) olarak işleyerek sınırsız derinlikte iç içe menüyü destekler
 */
const createMenuItemsFromConfig = (config, bulletColor, isSubmenu = false, favoriteHelpers = null, isCollapsed = false, navigate = null, onMenuClick = null) => {
    return config.map((item) => {
        const IconComponent = Icons[item.icon];
        const hasChildren = item.children && item.children.length > 0;

        // Pin butonu için label wrapper
        let labelContent = item.label;

        if (!hasChildren) {
            const itemPath = getPathFromKey(item.key);
            const isModalKey = MODAL_KEYS.includes(item.key);

            if (favoriteHelpers && !isCollapsed) {
                const { addFavorite, isFavorite } = favoriteHelpers;
                const isFav = isFavorite(item.key);

                labelContent = (
                    <div className="menu-item-with-pin">
                        <Link
                            to={isModalKey ? '#' : itemPath}
                            className="menu-item-link"
                            onClick={(e) => {
                                e.preventDefault();
                                if (isModalKey && onMenuClick) {
                                    onMenuClick(item.key);
                                } else {
                                    navigate(itemPath);
                                }
                            }}
                        >
                            <span className="menu-item-label">{item.label}</span>
                        </Link>
                        <Tooltip title={isFav ? "Favorilerden kaldır" : "Favorilere ekle"} mouseEnterDelay={1.5}>
                            <span
                                className={`pin-button ${isFav ? 'is-favorite' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    addFavorite({
                                        key: item.key,
                                        label: item.label,
                                        icon: item.icon,
                                    });
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        addFavorite({
                                            key: item.key,
                                            label: item.label,
                                            icon: item.icon,
                                        });
                                    }
                                }}
                            >
                                {isFav ? <PushpinFilled /> : <PushpinOutlined />}
                            </span>
                        </Tooltip>
                    </div>
                );
            } else {
                labelContent = (
                    <Link
                        to={isModalKey ? '#' : itemPath}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (isModalKey && onMenuClick) {
                                onMenuClick(item.key);
                            } else {
                                navigate(itemPath);
                            }
                        }}
                    >
                        {item.label}
                    </Link>
                );
            }
        }

        const menuItem = {
            key: item.key,
            label: labelContent,
            icon: IconComponent
                ? <span className="menu-icon"><IconComponent style={{ fill: bulletColor }} /></span>
                : (isSubmenu ? <DotIcon color={bulletColor} /> : null),
        };

        if (hasChildren) {
            menuItem.children = createMenuItemsFromConfig(item.children, bulletColor, true, favoriteHelpers, isCollapsed, navigate, onMenuClick);
        }
        return menuItem;
    });
};

const filterMenuConfig = (items, search) => {
    if (!search) return items;
    const lowerSearch = search.toLowerCase();
    return items
        .map((item) => {
            const matchedChildren = item.children
                ? filterMenuConfig(item.children, search)
                : undefined;
            if (
                item.label.toLowerCase().includes(lowerSearch) ||
                (matchedChildren && matchedChildren.length > 0)
            ) {
                return { ...item, children: matchedChildren };
            }
            return null;
        })
        .filter(Boolean);
};

const SideMenu = ({ collapsed, onMenuClick, selectedKey, onMenuToggle = () => { } }) => {
    const { themeTokens, isDark } = useContext(ThemeContext);
    const { addFavorite, removeFavorite, isFavorite } = useFavorites();
    const [searchText, setSearchText] = useState("");
    const [openKeys, setOpenKeys] = useState([]);
    const [isCollapsed, setIsCollapsed] = useState(collapsed);
    const navigate = useNavigate();

    // Favori işlemleri için helper obje - useMemo ile sarmalandı
    const favoriteHelpers = useMemo(() => ({
        addFavorite: (item) => {
            if (isFavorite(item.key)) {
                removeFavorite(item.key);
            } else {
                addFavorite(item);
            }
        },
        isFavorite,
    }), [addFavorite, removeFavorite, isFavorite]);

    useEffect(() => {
        setIsCollapsed(collapsed);
    }, [collapsed]);

    const textColor = isDark ? "#fff" : "#000";
    const bulletColor = themeTokens.colorPrimary;

    const filteredConfig = useMemo(
        () => filterMenuConfig(menuConfig, searchText),
        [searchText]
    );

    const filteredMenuItems = useMemo(
        () => createMenuItemsFromConfig(filteredConfig, bulletColor, false, favoriteHelpers, isCollapsed, navigate, onMenuClick),
        [filteredConfig, bulletColor, favoriteHelpers, isCollapsed, navigate, onMenuClick]
    );


    // Arama yapıldığında ilgili menüleri açık tutmak için eklendi
    useEffect(() => {
        if (searchText) {
            const allKeys = [];
            const collectKeys = (items) => {
                items.forEach(item => {
                    if (item.children && item.children.length > 0) {
                        allKeys.push(item.key);
                        collectKeys(item.children);
                    }
                });
            };
            collectKeys(filteredMenuItems);
            setOpenKeys((prev) => {
                if (prev.length === allKeys.length && prev.every((key, index) => key === allKeys[index])) {
                    return prev;
                }
                return allKeys;
            });
        }
    }, [searchText, filteredMenuItems]);


    // Accordion mode: Bir root menü açıldığında diğer root menüler kapanır
    const rootKeys = ['dashboard', 'financial', 'stock', 'ordertransaction', 'uts', 'users', 'reports', 'settings'];

    const onOpenChange = (keys) => {
        const latestOpenKey = keys.find(key => !openKeys.includes(key));

        if (latestOpenKey && rootKeys.includes(latestOpenKey)) {
            // Yeni açılan bir root menü varsa, sadece onu ve alt menülerini tut
            const newKeys = keys.filter(key =>
                !rootKeys.includes(key) || key === latestOpenKey
            );
            setOpenKeys(newKeys);
        } else {
            // Alt menü açılıyorsa normal davran
            setOpenKeys(keys);
        }
    };

    const handleToggle = () => {
        const newCollapsedState = !isCollapsed;
        setIsCollapsed(newCollapsedState);
        onMenuToggle(newCollapsedState);
    };

    return (
        <Sider
            collapsible
            collapsed={isCollapsed}
            trigger={null}
            width={280}
            collapsedWidth={60}
            style={{
                background: themeTokens.colorBgLayout,
                minHeight: "100vh",
                borderRight: `1px solid ${themeTokens.colorBorder}`,
                transition: "all 0.3s",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>

                {/* Logo */}
                <div
                    onClick={() => navigate("/dashboard")}
                    style={{
                        height: isCollapsed ? 48 : 64,
                        margin: isCollapsed ? 8 : 16,
                        background: themeTokens.colorBgLayout,
                        borderRadius: themeTokens.borderRadius,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        userSelect: "none",
                        transition: "all 0.3s",
                        cursor: "pointer",
                        flexShrink: 0,
                    }}
                >
                    <img
                        src={(process.env.PUBLIC_URL || "") + (
                            isDark
                                ? (isCollapsed ? "/gozluk3-3.png" : "/gozluk3.png")
                                : (isCollapsed ? "/gozluk1-1.png" : "/gozluk1.png")
                        )}
                        alt="Logo"
                        style={{
                            height: isCollapsed ? "32px" : "48px",
                            width: "auto",
                            transition: "all 0.3s",
                        }}
                    />
                </div>

                {/* Arama input */}
                {!isCollapsed && (
                    <div style={{ padding: "0 16px 8px 16px" }}>
                        <Input
                            placeholder="Ara..."
                            allowClear
                            size="middle"
                            onChange={(e) => setSearchText(e.target.value)}
                            value={searchText}
                            style={{
                                borderRadius: 12,
                                padding: "6px 12px",
                                width: "100%",
                                marginBottom: 8,
                                background: isDark ? "#2b2b2b" : "#f0f0f0",
                                border: `1px solid ${themeTokens.colorBorder}`,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                transition: "all 0.3s",
                                color: textColor
                            }}
                        />
                    </div>
                )}

                {/* Menü */}
                <div style={{ flex: 1, overflow: "auto" }}>
                    <Menu
                        mode="inline"
                        theme={isDark ? "dark" : "light"}
                        selectedKeys={[selectedKey]}
                        openKeys={openKeys}
                        onOpenChange={onOpenChange}
                        style={{
                            background: themeTokens.colorBgLayout,
                            color: textColor,
                            fontWeight: 500,
                            borderRadius: themeTokens.borderRadius,
                            transition: "all 0.3s",
                            border: "none"
                        }}
                        items={filteredMenuItems}
                    />
                </div>
                <div
                    onClick={handleToggle}
                    style={{
                        height: 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderTop: `1px solid ${themeTokens.colorBorder}`,
                        background: themeTokens.colorBgLayout,
                        transition: "all 0.3s",
                        color: themeTokens.colorPrimary,
                        fontSize: 18,
                        fontWeight: 600,
                        userSelect: "none"
                    }}
                >
                    {isCollapsed ? <Icons.RightOutlined /> : <Icons.LeftOutlined />}
                </div>
            </div>
        </Sider>
    );
};

// React.memo ile sarmalama - gereksiz re-render'ları önler
export default React.memo(SideMenu);
